import type { ReactNode } from 'react';
import type { ActionTone } from '@claims/core';
import { toneProps } from './action-tone.js';
import { pressWordsOf, usePress } from './press.js';

/** THE PRESSED BUTTON (ported 2026-10-18 from the medrec console's components/PressButton.tsx, its PR #176; press.ts): its own words until it
 *  is pressed, then a small spinner and the words as "-ing", disabled and `aria-busy` until the press settles. Either it runs its own press
 *  (`onPress`, guarded so a double click sends once), or it draws a press its owner runs (`busy`, for a form whose Enter key presses too, or
 *  a lock shared by a row's buttons). Every other attribute is a plain button's.
 *  `ringOnly`: a button in a row's tight slot (the Queue's inline Save beside its input), where the "-ing" words would be wider than the
 *  button and push the row onto a second line (the medrec console keeps its fixed row slots' size the same way): its busy face is the ring
 *  alone, shown at once and held 300 ms (press.ts `ringNow`), the words for a screen reader only.
 *  `tone`: what the press does, as its colour (ported 2026-10-25 from the medrec console's action-tones lane; core `action_tone.ts`). */
export function PressButton({ label, children, busy: given, onPress, disabled, className, title, ariaLabel, testId, ringOnly = false, tone }: {
  label: string; children?: ReactNode; busy?: boolean; onPress: () => Promise<unknown> | unknown; disabled?: boolean; className?: string; title?: string;
  ariaLabel?: string; testId?: string; ringOnly?: boolean; tone?: ActionTone | null;
}) {
  const own = usePress(onPress, { ringNow: ringOnly });
  const busy = given ?? own.busy;
  return (
    <button
      type="button"
      className={className}
      title={title}
      aria-label={ariaLabel}
      data-testid={testId}
      disabled={busy || disabled === true}
      aria-busy={busy ? true : undefined}
      data-busy={busy ? 'true' : undefined}
      {...toneProps(tone)}
      onClick={() => { void (given === undefined ? own.run() : onPress()); }}
    >
      {/* Both labels in one cell: the button is as wide as the wider, so the press moves nothing beside it; the one not shown is hidden from
          sight and from a screen reader. */}
      <span className={ringOnly ? 'press-stack press-ring-only' : 'press-stack'}>
        <span className="press-idle" aria-hidden={busy ? true : undefined}>{children ?? label}</span>
        <span className="press-busy" aria-hidden={busy ? undefined : true}><span className="press-spin" aria-hidden="true" data-testid={busy ? 'press-spin' : undefined} />
          {ringOnly ? <span className="sr">{pressWordsOf(label)}</span> : pressWordsOf(label)}</span>
      </span>
    </button>
  );
}
